<!-- src/components/MaintenanceGate.vue -->
<template>
  <div
    v-if="showGate"
    class="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center"
    role="dialog"
    aria-modal="true"
    aria-labelledby="maint-title"
  >
    <div class="bg-white rounded-xl p-6 max-w-md w-[90%] text-center space-y-4 shadow-xl">
      <h2 id="maint-title" class="text-xl font-semibold">Mantenimiento programado</h2>

      <p v-if="status?.message" class="text-gray-700">{{ status.message }}</p>
      <p v-else class="text-gray-700">Habrá una ventana de mantenimiento próximamente.</p>

      <div class="text-xs text-gray-500 space-y-1">
        <div v-if="status?.startsAt">Inicio: {{ fmt(status.startsAt) }}</div>
        <div v-if="status?.endsAt">Fin estimado: {{ fmt(status.endsAt) }}</div>
      </div>

      <div class="flex items-center justify-center mt-2">
        <div class="relative w-20 h-20">
          <svg viewBox="0 0 100 100" class="w-20 h-20">
            <circle cx="50" cy="50" r="42" stroke="rgba(0,0,0,.1)" stroke-width="8" fill="none" />
            <circle
              cx="50" cy="50" r="42"
              :stroke="progressColor"
              stroke-width="8" fill="none" stroke-linecap="round"
              :style="progressStyle"
              transform="rotate(-90 50 50)"
            />
          </svg>
        </div>
      </div>

      <div class="text-[11px] text-gray-500">
        Este aviso se cerrará automáticamente en ~{{ remainingSeconds }}s
      </div>

      <button
        type="button"
        class="mt-1 px-4 py-2 rounded bg-blue-600 text-white"
        @click="acceptNow"
      >
        Aceptar ahora
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRoute } from 'vue-router';
import { MaintenanceService } from '@/views/panelMantenimiento/Services/MaintenanceService';

const SESSION_DISMISS_KEY = 'maint_gate_dismissed_v1';

const ALLOW = [
  /^\/login$/,
  /^\/panel-mantenimiento/,
  /^\/mantenimiento$/,
  /^\/registrarse$/,
  /^\/solicitar-recuperacion$/,
  /^\/expired-recovery$/,
  /^\/cambiar_pass$/,
  /^\/magic-link-login(\/[\w-]+)?$/,
  /^\/registro-control(\/[\w-]+)?$/
];

export default {
  name: 'MaintenanceGate',
  props: {
    showWhenActive: { type: Boolean, default: false }
  },
  setup(props) {
    const status = ref(null);
    const showGate = ref(false);
    const dismissed = ref(false);
    const route = useRoute();

    const progress = ref(0);
    const durationMs = 10000;
    let rafId = null;
    let startedAt = 0;

    const r = 42;
    const circumference = 2 * Math.PI * r;
    const strokeDashoffset = computed(() => {
      const p = Math.min(100, Math.max(0, progress.value));
      return circumference * (1 - p / 100);
    });
    const progressStyle = computed(() => ({
      strokeDasharray: `${circumference}px`,
      strokeDashoffset: `${strokeDashoffset.value}px`,
      transition: 'stroke-dashoffset 0.08s linear'
    }));
    const progressColor = computed(() => 'var(--color-primary, #2563EB)');
    const remainingSeconds = computed(() => Math.max(0, Math.ceil((100 - progress.value) / 10)));

    function fmt(iso) {
      try {
        return new Date(iso).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' });
      } catch {
        return new Date(iso).toLocaleString();
      }
    }

    function isAllowed(path) {
      return ALLOW.some((rx) => rx.test(path));
    }

    function decideVisibility() {
      if (!status.value || isAllowed(route.path)) {
        showGate.value = false;
        return;
      }
      const s = status.value;
      const shouldShow = s.enabled && (s.upcoming || (s.active && props.showWhenActive));
      showGate.value = shouldShow && !dismissed.value;
    }

    function lockScroll(on) {
      document.documentElement.classList.toggle('no-scroll', on);
    }

    function startTimer() {
      stopTimer();
      progress.value = 0;
      startedAt = performance.now();
      tick();
    }
    function tick() {
      const now = performance.now();
      const pct = Math.min(100, ((now - startedAt) / durationMs) * 100);
      progress.value = pct;
      if (pct >= 100) {
        acceptNow();
        return;
      }
      rafId = requestAnimationFrame(tick);
    }
    function stopTimer() {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
      lockScroll(false);
    }

    function acceptNow() {
      dismissed.value = true;
      try { sessionStorage.setItem(SESSION_DISMISS_KEY, '1'); } catch { /* noop */ }
      showGate.value = false;
      stopTimer();
    }

    async function refresh() {
      const s = await MaintenanceService.getStatus();
      status.value = s;

      if (!(s.enabled && (s.active || s.upcoming))) {
        try { sessionStorage.removeItem(SESSION_DISMISS_KEY); } catch { /* noop */ }
        dismissed.value = false;
      }

      decideVisibility();

      if (showGate.value) {
        lockScroll(true);
        startTimer();
      } else {
        stopTimer();
      }
    }

    onMounted(async () => {
      try { dismissed.value = !!sessionStorage.getItem(SESSION_DISMISS_KEY); } catch { /* noop */ }
      await refresh();
    });

    onBeforeUnmount(() => stopTimer());

    return {
      status,
      showGate,
      fmt,
      progressStyle,
      progressColor,
      remainingSeconds,
      acceptNow
    };
  }
};
</script>